import styles from './template.css';
import template from './template';
import {storeInstance} from '@aofl/store/dist/legacy';
import {AoflElement, customElement, property} from '@aofl/element';
import {i18nMixin} from '@aofl/i18n-mixin';
import translations from '../i18n';
import {sdoNs, licenseAssignMode} from '../constants-enumerate';
import {ApiService} from '../api-service';
import '../sdo-licenses';

/**
 * @summary LicenseAssign
 * @extends {AoflElement}
 */
@customElement('license-assign')
class LicenseAssign extends i18nMixin(AoflElement) {
  static is = 'license-assign';
  /**
   * Creates an instance of LicenseAssign.
   */
  constructor() {
    super();
    this.translations = translations;
  }

  @property({
    store: storeInstance,
    mapState: `${sdoNs.LICENSES}.$licenses`
  })
  licenses = [];

  @property({type: Object})
  selectedLicenseInfo = {};

  @property({
    store: storeInstance,
    mapState: `${sdoNs.LICENSES}.$licensesByProduct`
  })
  licensesByProduct = {};

  @property({type: Array, store: storeInstance, mapState: `${sdoNs.LICENSES}.licenses`})
  licensesFromApi = [];

  @property({type: Object})
  estimating = {};

  @property()
  estimates = {};

  @property()
  errors = {};

  @property()
  disableAssign = true;

  @property({type: Boolean, attribute: false})
  uploading = false;

  @property({type: String, mapState: `${sdoNs.LICENSE_ASSIGN}.objectType`, store: storeInstance})
  objectType = '';
  @property({type: String, mapState: `${sdoNs.LICENSE_ASSIGN}.objectIds`, store: storeInstance})
  objectIds = [];
  @property({type: Boolean, mapState: `${sdoNs.SESSION}.settings.aofl_products`, store: storeInstance})
  aoflProducts = {};
  @property({type: Object, mapState: `${sdoNs.SESSION}.settings`, store: storeInstance})
  settings = {};
  @property({type: String, attribute: false})
  screen = 'selection';
  @property({type: Object, attribute: false})
  studentLicenseResults = {};
  @property({type: Object})
  conflictCount = {};
  @property({type: Boolean})
  orgHasProducts = false;
  @property({type: Boolean})
  showErrorMsg = false;

  // Selected licenses for students after they clicked the assign button and is returned to student modal
  @property({type: Object})
  prevSelectedLicenses = {};

  async connectedCallback() {
    super.connectedCallback();

    const state = storeInstance.getState();
    this.orgHasProducts = Object.entries(state[sdoNs.SESSION].settings.aofl_products)
      .filter((entry) => entry[1]).length > 0;
    const filters = {};
    filters.group_id = state[sdoNs.SESSION].group_info.id;
    filters.hide_expired = true;
    const result = await ApiService.licenseSearch({limit: 1000}, filters);
    for (const [product, licenseId] of Object.entries(this.prevSelectedLicenses)) {
      await this.toggle(product, licenseId);
    }

    storeInstance.commit({
      namespace: sdoNs.LICENSES,
      mutationId: 'setLicenses',
      payload: result.results
    });
  }

  back() {
    this.changeChange('selection');
  }

  changeChange(screen) {
    this.screen = screen;
    this.dispatchEvent(new CustomEvent('screen-change', {
      composed: true,
      bubbles: true,
      detail: {screen}
    }));
  }

  async confirmation() {
    let needsConfirmation = false;
    if (this.objectType === 'student' && this.objectIds?.length === 1) {
      // check specific case for individual student
      this.studentLicenseResults = await ApiService.licenseCheckCompatibility({
        student_id: this.objectIds[0],
        license_ids: this.selectedLicenseInfo,
      });

      needsConfirmation = this.studentLicenseResults.has_incompatibility;
      if (!needsConfirmation) {
        needsConfirmation = Object.values(this.selectedLicenseInfo).some((licenseId) => {
          const result = this.studentLicenseResults.licenses[licenseId];
          return result.status !== 'unassigned' && result.status !== 'expired';
        });
      }
    } else {
      needsConfirmation = Object.values(this.estimates).some((estimate) => estimate.active_license_count > 0);
    }

    if (Object.keys(this.conflictCount).length > 0) {
      needsConfirmation = true;
    }

    if (needsConfirmation) {
      return this.changeChange('confirmation');
    }

    return this.assignLicenses();
  }

  getApiArgs(licenseId) {
    const args = {
      license_id: parseInt(licenseId, 10)
    };
    const state = storeInstance.getState();
    const licenseAssignState = state[sdoNs.LICENSE_ASSIGN];
    if (licenseAssignState.assignMode === licenseAssignMode.SELECTED) {
      if (state[sdoNs.LICENSE_ASSIGN].studentIds) {
        args.object_ids = state[sdoNs.LICENSE_ASSIGN];
        args.object_type = 'student';
      }
      if (licenseAssignState.rosterId) {
        args.roster_job_id = licenseAssignState.rosterId;
      }
      if (state[sdoNs.LICENSE_ASSIGN].groupIds) {
        args.object_ids = state[sdoNs.LICENSE_ASSIGN].groupIds;
      }
      if (licenseAssignState.objectIds) {
        args.object_ids = licenseAssignState.objectIds;
      }
    } else if (licenseAssignState.filtered) {
      args.filtered = licenseAssignState.filtered;
    }

    if (licenseAssignState.objectType) {
      args.object_type = licenseAssignState.objectType;
    }

    return args;
  }

  checkEnableAssign() {
    if (Object.keys(this.selectedLicenseInfo).length === 0) {
      this.disableAssign = true;
      return;
    }

    const errorCodes = Object.keys(this.errors);
    let hasErrors = false;
    for (const [_, licenseId] of Object.entries(this.selectedLicenseInfo)) {
      if (errorCodes.indexOf(String(licenseId)) !== -1) {
        hasErrors = true;
        break;
      }

      if (this.estimates[licenseId].total_license_cost < 1) {
        hasErrors = true;
        break;
      }
    }

    this.disableAssign = hasErrors;
  }

  async toggle(product, licenseId) {
    const selected = Object.assign({}, this.selectedLicenseInfo);
    if (this.selectedLicenseInfo[product] && this.selectedLicenseInfo[product] === licenseId) {
      delete selected[product];
    } else {
      selected[product] = licenseId;
    }

    if (this.selectedLicenseInfo[product]) {
      delete this.errors[this.selectedLicenseInfo[product]];
      delete this.conflictCount[this.selectedLicenseInfo[product]];
    }

    this.selectedLicenseInfo = selected;

    if (selected[product]) {
      this.disableAssign = true;
      this.estimating[licenseId] = true;
      this.requestUpdate();

      const state = storeInstance.getState();
      const licenseAssignState = state[sdoNs.LICENSE_ASSIGN];

      if (licenseAssignState.newStudent) {
        const index = this.licenses.findIndex((license) => license.id === licenseId);
        if (this.licenses[index].group_available < 1) {
          this.errors[licenseId] = true;
        }
        this.estimates[licenseId] = {
          total_license_cost: 1
        };
      } else {
        const estimate = await ApiService.licenseGetInvoice(this.getApiArgs(licenseId));
        if (estimate.available_license_inventory < estimate.total_license_cost) {
          this.errors[licenseId] = true;
        }
        this.estimates[licenseId] = estimate;
        if (this.estimates[licenseId].total_license_cost === 0) {
          this.errors[licenseId] = true;
        }
        if (estimate.linked_student_count > 0) {
          this.conflictCount[licenseId] = estimate.linked_student_count;
        }
      }
      this.estimating[licenseId] = false;

      this.requestUpdate();
    }

    this.showErrorMsg = Object.keys(this.errors).length > 0;

    this.checkEnableAssign();
  }

  async assignLicenses() {
    this.uploading = true;
    const args = [];
    const licenseInfo = {};
    this.disableAssign = true;
    const selectedLicenseInfo = this.selectedLicenseInfo;
    for (const [_, licenseId] of Object.entries(this.selectedLicenseInfo)) {
      if (typeof this.errors[licenseId] !== 'undefined') continue;

      this.licensesFromApi.map((info) => {
        if (licenseId === info.id) {
          licenseInfo[licenseId] = info;
        }
      });

      args.push(this.getApiArgs(licenseId));
    }
    this.showErrorMsg = false;
    this.dispatchEvent(new CustomEvent('on-submit', {
      composed: true,
      detail: {
        args,
        licenseInfo,
        selectedLicenseInfo
      }
    }));
  }

  /**
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default LicenseAssign;
